import { trackError } from '@atlassian/help-center-common-util/analytics';
import { fetchJson } from '@atlassian/help-center-common-util/fetch';
import { contextPath } from '@atlassian/help-center-common-util/history';
import type { PublicToInternalPropertyTransformerContext } from '@atlassian/ui-modifications-core/common/types';
import type { LookupUser, LookupUsers } from '@atlassian/ui-modifications-core/types/lookup';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils/field-value-lookup-error';
import { isNotAutomaticId } from '@atlassian/ui-modifications-core/utils/is-not-automatic-id';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import type { RequestCreateExecutionContext } from '../../../types/context';
import type { UserPickerExtraData } from '../../../types/extra-data';

export type UserPickerPublicSetValue = null | string;

export type UserPickerPublicGetValue = null | {
	accountId: string;
};

export interface UserPickerInternalValue {
	label: string;
	email: string;
	iconUrl?: string;
	accountId: string;
	value: string;
}

export type UserPickerTransformedValue = UserPickerInternalValue | null;

/**
 * Finds a user in the lookup data by their account ID.
 *
 * @param lookupData - Array of users to search through
 * @param accountId - The account ID to search for
 * @returns The matching user object or null if not found
 */
const findUserLookupValue = (lookupData: LookupUsers, accountId: string): LookupUser | null => {
	if (!lookupData || !Array.isArray(lookupData)) {
		return null;
	}

	return lookupData.find((user) => user.accountId === accountId) || null;
};

/**
 * Validates that a value is a valid user picker set value.
 * A valid value is either null or a non-empty string (account ID) that is not an automatic ID.
 *
 * @param value - The value to validate
 * @returns True if the value is valid, false otherwise
 */
export const validateUserPickerSetValue = (value: unknown): value is UserPickerPublicSetValue =>
	value === null || (isNotEmptyString(value) && isNotAutomaticId(value));

/**
 * Transforms a public user picker value (account ID) into an internal user object.
 * Looks up the user details from the provided lookup data and creates a complete user object.
 *
 * @param value - The public value to transform (account ID or null)
 * @param lookupValues - Array of user lookup data to search through
 * @returns Transformed user object with all user details, or null if value is null
 * @throws {FieldValueLookupError} When the user cannot be found in the lookup data
 */
export const userPickerSetValueTransformer = (
	value: UserPickerPublicSetValue,
	lookupValues?: LookupUsers,
): UserPickerTransformedValue => {
	if (value === null) {
		return null;
	}

	const foundUser = findUserLookupValue(lookupValues || [], value);

	if (!foundUser) {
		throw new FieldValueLookupError(`Could not find user with accountId: ${value}`);
	}

	return {
		label: foundUser.name,
		email: '',
		iconUrl: foundUser.picture,
		accountId: foundUser.accountId,
		value: foundUser.accountId,
	};
};

/**
 * Reads the user picker's extra data from a transformer's field context. The core types extra data
 * as an open-ended record, so this checks the shape at runtime and returns it as
 * `UserPickerExtraData`, the same type used when the fields are initialized.
 */
export const getUserPickerExtraData = (
	fieldContext?: PublicToInternalPropertyTransformerContext['fieldContext'],
): UserPickerExtraData => {
	const fieldConfigId = fieldContext?.extraData?.fieldConfigId;
	return typeof fieldConfigId === 'string' && fieldConfigId ? { fieldConfigId } : {};
};

interface PortalUserSearchResponse {
	avatar?: string;
	displayName: string;
	emailAddress?: string;
	accountId: string;
}

/**
 * Transforms a public user picker value (account ID) into an internal user object, using the
 * Portal's own user search.
 *
 * The Portal user search applies the same rules as the Portal user picker: it only returns users the
 * viewer is allowed to select, and their email only when the viewer is allowed to see it. So a
 * `setValue` can only set users the viewer could have selected in the UI.
 *
 * @param value - The public value to transform (account ID or null)
 * @param _lookupValues - Unused: this transformer fetches its own data
 * @param transformerContext - Provides the portal ID and the field ID (sent as the field name)
 * @returns Transformed user object with all user details, or null if value is null
 * @throws {FieldValueLookupError} When the Portal user search doesn't return the user
 */
export const userPickerPortalSetValueTransformer = async (
	value: UserPickerPublicSetValue,
	_lookupValues?: unknown,
	transformerContext?: PublicToInternalPropertyTransformerContext<RequestCreateExecutionContext>,
): Promise<UserPickerTransformedValue> => {
	if (value === null) {
		return null;
	}

	const portalId = transformerContext?.executionContext?.portal?.id;
	const fieldName = transformerContext?.fieldContext?.fieldId;

	if (!portalId || !fieldName) {
		throw new FieldValueLookupError(`Could not find user with accountId: ${value}`);
	}

	const searchParams = new URLSearchParams({ fieldName, query: value });
	// The field's configuration applies its user filter (groups or project roles), so the
	// search only returns users the Portal picker would offer
	const { fieldConfigId } = getUserPickerExtraData(transformerContext?.fieldContext);
	if (fieldConfigId) {
		searchParams.append('fieldConfigId', fieldConfigId);
	}
	const queryString = searchParams.toString();
	const uri = `${contextPath}/rest/servicedesk/1/customer/portal/${portalId}/user-search?${queryString}`;

	let users: PortalUserSearchResponse[];
	try {
		users = await fetchJson<PortalUserSearchResponse[]>(uri, { method: 'GET' });
	} catch (error) {
		trackError('finduser.fetch.failed', {}, error instanceof Error ? error : undefined);
		throw error;
	}

	// The search matches on more than the account ID, so only an exact match counts
	const foundUser = Array.isArray(users)
		? users.find((user) => user.accountId === value)
		: undefined;

	if (!foundUser) {
		throw new FieldValueLookupError(`Could not find user with accountId: ${value}`);
	}

	return {
		label: foundUser.displayName,
		email: foundUser.emailAddress ?? '',
		iconUrl: foundUser.avatar,
		accountId: foundUser.accountId,
		value: foundUser.accountId,
	};
};

/**
 * Transforms an internal user object back to a public get value.
 * Extracts only the account ID from the complete user object for public API consumption.
 *
 * @param value - The internal user object to transform
 * @returns Public get value containing only the account ID, or null if value is falsy
 */
export const userPickerGetValueTransformer = (
	value: UserPickerTransformedValue,
): UserPickerPublicGetValue => {
	if (!value || !value.accountId) {
		return null;
	}

	return { accountId: value.accountId };
};

/**
 * Converts a transformed user object back to its basic form (account ID).
 * This is used to simplify the user object back to just the essential identifier.
 *
 * @param value - The transformed user object to convert, or null
 * @returns The account ID as a string, or null if the input value is falsy
 */
export const modifyUserPickerValueToBasic = (
	value: UserPickerTransformedValue | null,
): UserPickerPublicSetValue => {
	if (!value) {
		return null;
	}
	return value.accountId;
};
